import React, { useState } from 'react';
import SEO from '../components/layout/SEO';
import { Search, BookOpen, Video, MessageSquare, Phone, Mail, FileText, Users, Settings, BarChart3 } from 'lucide-react';

const Help: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    {
      icon: BookOpen,
      title: 'Getting Started',
      description: 'Learn the basics of using Lernix LMS',
      articles: [
        'How to create your first course',
        'Setting up student accounts',
        'Understanding the dashboard',
        'Basic navigation guide'
      ]
    },
    {
      icon: Users,
      title: 'User Management',
      description: 'Manage students, teachers, and administrators',
      articles: [
        'Adding new students',
        'Managing teacher accounts',
        'Setting up parent access',
        'Role-based permissions'
      ]
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reports',
      description: 'Understanding your data and insights',
      articles: [
        'Viewing student progress',
        'Generating reports',
        'Understanding analytics',
        'Exporting data'
      ]
    },
    {
      icon: Settings,
      title: 'System Settings',
      description: 'Configure your LMS settings',
      articles: [
        'Customizing your theme',
        'Notification settings',
        'Security configurations',
        'Integration setup'
      ]
    }
  ];

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer: 'You can reset your password by clicking the "Forgot Password" link on the login page. You\'ll receive an email with instructions to create a new password.'
    },
    {
      question: 'Can I use Lernix LMS on mobile devices?',
      answer: 'Yes! Lernix LMS is fully responsive and works on all devices including smartphones and tablets. You can access all features from your mobile browser.'
    },
    {
      question: 'How do I contact support?',
      answer: 'You can contact our support team through email at support@ciphermind.tech, by phone at +92 (308) 7700430, or through the contact form on our website.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use industry-standard encryption and security measures to protect your data. All information is encrypted in transit and at rest.'
    },
    {
      question: 'Can I export my course data?',
      answer: 'Yes, you can export your course data, student information, and reports in various formats including CSV and PDF from the analytics dashboard.'
    },
    {
      question: 'What happens to my data if I cancel my subscription?',
      answer: 'Your data is retained for 30 days after cancellation. You can reactivate your account within this period to restore access to your data.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Help Center - Lernix LMS"
        description="Get help and support for Lernix LMS. Find answers to common questions and learn how to use our platform."
        keywords="help center, support, FAQ, documentation, Lernix LMS"
      />
      
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Help
              <span className="text-primary-600 block">Center</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Find answers to your questions and learn how to get the most out of Lernix LMS.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search for help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Browse by Category
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <category.icon size={24} className="text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {category.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {category.description}
                </p>
                <ul className="space-y-2">
                  {category.articles.map((article, articleIndex) => (
                    <li key={articleIndex} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                      {article}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary-600 rounded-2xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-6">
              Still Need Help?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Our support team is here to help you succeed with Lernix LMS.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center space-y-3">
                <Mail size={32} className="text-primary-200" />
                <h3 className="font-semibold">Email Support</h3>
                <p className="text-primary-100 text-sm">support@ciphermind.tech</p>
              </div>
              <div className="flex flex-col items-center space-y-3">
                <Phone size={32} className="text-primary-200" />
                <h3 className="font-semibold">Phone Support</h3>
                <p className="text-primary-100 text-sm">+92 (308) 7700430</p>
              </div>
              <div className="flex flex-col items-center space-y-3">
                <MessageSquare size={32} className="text-primary-200" />
                <h3 className="font-semibold">Live Chat</h3>
                <p className="text-primary-100 text-sm">Available 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Help; 